import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getPetSelector,
  getIsUpdatingPetProfileSelector,
  getPetProfileUpdateErrorSelector,
} from '@modules/subscriptions/core/selectors';
import { isNearAdultTransition } from '@modules/subscriptions/utils/getEffectiveDogStage';
import { PET_UPDATE_REQUIRED_COMPLETED_EVENT, getCompletedStorageKey } from './constants';
import { readStorageFlag, writeStorageFlag } from './utils';

interface UsePetUpdateRequiredResult {
  /** The pet is flagged and the fix has not yet been completed. */
  shouldShow: boolean;
  petId?: string;
  /** Persist + broadcast completion so all instances hide immediately. */
  markCompleted: () => void;
}

/**
 * Centralises the gating logic for the "update-required" flow.
 *
 * The flow is visible only while the pet is flagged
 * `food_recommendation.update_required` by the backend and the corrective
 * update has not yet been completed. Completion is tracked in localStorage (and
 * broadcast across instances) because the flag is cleared server-side and won't
 * reflect locally until the next refetch.
 *
 * The puppy→adult transition flow takes priority: while the pet is within the
 * transition window (`isNearAdultTransition`), this flow stays hidden so the
 * two prompts never compete. Completing the transition recalculates the
 * product anyway, which typically clears `update_required` server-side.
 */
export function usePetUpdateRequired(): UsePetUpdateRequiredResult {
  const pet = useSelector(getPetSelector);
  const petId = pet?.id;
  const completedKey = petId != null ? getCompletedStorageKey(petId) : null;

  const [completed, setCompleted] = useState<boolean>(() => readStorageFlag(completedKey));

  // Re-read when the pet (and therefore the key) changes.
  useEffect(() => {
    setCompleted(readStorageFlag(completedKey));
  }, [completedKey]);

  // Stay in sync with sibling instances (modal ↔ banner).
  useEffect(() => {
    const handler = () => setCompleted(true);
    window.addEventListener(PET_UPDATE_REQUIRED_COMPLETED_EVENT, handler);
    return () => window.removeEventListener(PET_UPDATE_REQUIRED_COMPLETED_EVENT, handler);
  }, []);

  const markCompleted = useCallback(() => {
    writeStorageFlag(completedKey);
    setCompleted(true);
    window.dispatchEvent(new CustomEvent(PET_UPDATE_REQUIRED_COMPLETED_EVENT));
  }, [completedKey]);

  const shouldShow =
    !!pet?.food_recommendation?.update_required && !completed && !isNearAdultTransition(pet);

  return { shouldShow, petId, markCompleted };
}

/**
 * Marks the flow complete once the corrective update succeeds.
 *
 * Detection is scoped to `active` (our recalculation modal being open) so we
 * don't mistake an unrelated pet update — e.g. a name change, which also
 * toggles `isUpdating` — for completion. The "active" flag is captured in a ref
 * that persists past the modal closing so the success transition is never lost.
 */
export function useMarkCompletedOnUpdateSuccess(active: boolean, markCompleted: () => void): void {
  const isUpdating = useSelector(getIsUpdatingPetProfileSelector);
  const error = useSelector(getPetProfileUpdateErrorSelector);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (active && isUpdating) {
      submittedRef.current = true;
    }
  }, [active, isUpdating]);

  useEffect(() => {
    if (!submittedRef.current) return;

    if (error) {
      // Allow a retry after a failed attempt.
      submittedRef.current = false;
      return;
    }

    if (!isUpdating) {
      submittedRef.current = false;
      markCompleted();
    }
  }, [isUpdating, error, markCompleted]);
}
